import React from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import { Card, CardContent, Typography, Box, ButtonGroup, Button, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';

const prepareChartData = (statistics, _format) => {
    if (!statistics || statistics.length === 0) {
        return {
            labels: [],
            datasets: []
        };
    }

    const labels = [];
    const data = [];

    statistics.forEach(stat => {
        const dateObj = dayjs(stat.date);
        let formattedDate;
        if (_format === 'month') {
            formattedDate = dateObj.format('MMM YYYY');
        } else {
            formattedDate = dateObj.format('MMM D');
        }
        labels.push(formattedDate);
        data.push(stat.count || 0);
    });

    return {
        labels: labels,
        datasets: [
            {
                data: data,
                borderColor: '#1976d2',
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                tension: 0.4,
                fill: true
            }
        ]
    };
};

export default function StudentsRegistrationChart({
    statistics,
    loading,
    title = "Student Registrations",
    onDateRangeChange,
    selectedRange,
    format = 'day'
}) {
    const data = prepareChartData(statistics, format);
    const dateRanges = [
        { label: 'Last 7 Days', value: '7_days', format: 'day' },
        { label: 'Last 30 Days', value: '30_days', format: 'day' },
        { label: 'Last Year', value: 'year', format: 'month' }
    ];

    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (!data || !data.labels || data.labels.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: { xs: 1, sm: 0 },
                        mb: 2
                    }}>
                        <Typography variant="h6">{title}</Typography>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: { xs: 'center', sm: 'flex-end' },
                            width: { xs: '100%', sm: 'auto' }
                        }}>
                            <ButtonGroup size="small" variant="outlined">
                                {dateRanges.map((range) => (
                                    <Button
                                        key={range.value}
                                        variant={selectedRange === range.value ? 'contained' : 'outlined'}
                                        onClick={() => onDateRangeChange(range.value)}
                                    >
                                        {range.label}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </Box>
                    </Box>
                    <Typography>No data available for the selected period</Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent>
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 1, sm: 0 },
                    mb: 2,
                    px: 2
                }}>
                    <Typography variant="h6">{title}</Typography>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: { xs: 'center', sm: 'flex-end' },
                        width: { xs: '100%', sm: 'auto' }
                    }}>
                        <ButtonGroup size="small" variant="outlined">
                            {dateRanges.map((range) => (
                                <Button
                                    key={range.value}
                                    variant={selectedRange === range.value ? 'contained' : 'outlined'}
                                    onClick={() => onDateRangeChange(range.value)}
                                >
                                    {range.label}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </Box>
                </Box>
                <Box sx={{ width: '100%' }}>
                    <LineChart
                        xAxis={[{
                            scaleType: 'point',
                            data: data.labels,
                            label: ''
                        }]}
                        series={data.datasets.map(dataset => ({
                            data: dataset.data,
                            label: dataset.label,
                            color: dataset.borderColor,
                            curve: 'catmullRom'
                        }))}
                        height={400}
                    />
                </Box>
            </CardContent>
        </Card>
    );
}
